import { useRef, useState } from 'react'

import { Box, Button, Form } from '@factorialco/deprecated-design-system'
import { RichTextEditor } from '@factorialco/f0-react/dist/experimental'
import { FormikProvider } from 'formik'
import {
  AtsEvaluationFormEditDocument,
  AtsEvaluationFormEditMutation,
  AtsFeedbackDetailFragment,
  AtsJobApplicationNotesDocument,
  EvaluationFormListItemFragment,
} from 'generated/resources/graphql'
import { isEqual } from 'lodash'

import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { FieldArray, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { AnswerItem } from 'modules/ats/components/EvaluationForm/components/EvaluationFormItem/AnswerItem'
import { VisibilityToggler } from 'modules/ats/components/Feedbacks/VisibilityToggler'
import { calculateAverageRating } from 'modules/ats/lib/utils/evaluationForms'
import { AtsFeedbackFormValues, toVisibilityEnum } from 'modules/ats/lib/utils/feedbacks'
import {
  useNotesMentions,
  useRichTextEditorNotesMentions,
} from 'modules/ats/lib/utils/useNotesMentions'

type AtsEvaluationFormAnswer = NonNullable<
  NonNullable<AtsFeedbackDetailFragment['evaluationFormAnswers']>[0]
>

interface Props {
  feedback: AtsFeedbackDetailFragment
  evaluationForm: EvaluationFormListItemFragment
  onEditFinish: () => void
  restrictedCommentsAvailable?: boolean
}

interface AtsEvaluationFormValues extends AtsFeedbackFormValues {
  evaluationFormAnswers: Record<string, AtsEvaluationFormAnswer>
}

export const EvaluationFormEdit = ({
  feedback,
  evaluationForm,
  onEditFinish,
  restrictedCommentsAvailable,
}: Props) => {
  const [updateFeedback, { loading }] = useMutation(AtsEvaluationFormEditDocument, {
    extractorFn: (data: AtsEvaluationFormEditMutation) => data.atsMutations.updateAtsFeedback,
    refetchQueries: [AtsJobApplicationNotesDocument],
  })
  const [accessesIds, setAccessesIds] = useState<string[] | null>([])
  const editorRef = useRef<EditorHandle>(null)
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()
  const { candidate, applicationPhase, application, description, evaluationFormAnswers, rating } =
    feedback
  const uniqueMentioningAccesses = useNotesMentions({
    atsJobPostingId: application?.jobPosting.id,
    candidateId: candidate.id,
  })
  const {
    accesses: uniqueRichTextEditorMentioningAccesses,
    loading: richTextEditorMentionsLoading,
  } = useRichTextEditorNotesMentions({
    atsJobPostingId: application?.jobPosting.id,
    candidateId: candidate.id,
  })
  const parsedAnswers = evaluationFormAnswers?.reduce((all, answer) => {
    return { ...all, [answer.questionId]: answer }
  }, {})

  const onFiles = useUnsafeCallback(
    (attachments) => {
      if (isEqual(attachments, form.values.attachments)) {
        return
      }
      form.setFieldValue('attachments', attachments)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEditFinish]
  )

  // TODO: Make files updates & delete work
  const form = useForm<AtsEvaluationFormValues>({
    defaultValues: {
      description,
      rating,
      atsCandidateId: candidate.id,
      atsApplicationId: application?.id,
      atsApplicationPhaseId: applicationPhase?.id,
      attachments: [],
      mentionIds: accessesIds,
      evaluationFormAnswers: parsedAnswers || {},
      visibility: toVisibilityEnum(feedback.visibility),
    },
    async onSubmit(data, { setErrors }) {
      const answers = Object.values(data.evaluationFormAnswers).map((answer) => ({
        questionId: answer.questionId,
        note: answer.note,
        score: answer.score,
      }))

      const result = await updateFeedback({
        variables: {
          id: feedback.id,
          ...data,
          atsApplicationPhaseId: applicationPhase?.id,
          evaluationFormAnswers: answers,
          rating: calculateAverageRating(answers.map((answer) => answer.score)),
          attachments: data.attachments && data.attachments.length > 0 ? data.attachments : null,
        },
      })

      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.evaluation_forms.edit.success_message'),
        })
      }

      onEditFinish()

      return handled
    },
  })

  return (
    <FormikProvider value={form}>
      <Form onSubmit={() => form.handleSubmit()} padding={null}>
        <Box gap="s8" flexDirection="row" alignItems="center" maxWidth="s600">
          <UserAvatar user={feedback.access || undefined} size="s40" />
        </Box>

        <Box gap="s8">
          <Box
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
            borderRadius={{ all: 'abs008' }}
            background="grey000White"
            gapDivider
          >
            <FieldArray
              name="answers"
              render={() =>
                evaluationForm.questions.map((question) => (
                  <AnswerItem key={question.id} question={question} />
                ))
              }
            />
          </Box>
        </Box>

        {canUseRichTextEditor && restrictedCommentsAvailable && (
          <Box flexDirection="row" justifyContent="flexEnd">
            <VisibilityToggler
              value={form.values.visibility}
              onChange={(value) => form.setFieldValue('visibility', value)}
            />
          </Box>
        )}

        {richTextEditorLoading || richTextEditorMentionsLoading ? (
          <RichTextEditor.Skeleton />
        ) : canUseRichTextEditor ? (
          <RichTextEditor
            {...editorConfig}
            title={evaluationForm?.name}
            onChange={(data) => {
              form.setFieldValue('description', data?.value)
              setAccessesIds(data?.mentionIds?.map(String) ?? null)
            }}
            placeholder={i18n.t('ats.feedbacks.placeholder')}
            mentionsConfig={{
              users: uniqueRichTextEditorMentioningAccesses,
            }}
            initialEditorState={{ content: form.values.description ?? '' }}
            filesConfig={{
              onFiles: onFiles,
              multipleFiles: true,
            }}
          />
        ) : (
          <TextEditor
            ref={editorRef}
            onChange={(data) => {
              if (data && typeof data !== 'string') {
                form.setFieldValue('description', data.value)
                setAccessesIds(data?.ids?.map(String) ?? null)
              }
            }}
            onFiles={onFiles}
            placeholder={i18n.t('ats.feedbacks.placeholder')}
            initialEditorState={{
              data: form.values.description || '',
              files: form.values.attachments || [],
            }}
            height="s200"
            hasMentions
            users={uniqueMentioningAccesses}
            enableBetaFeatures={false}
          />
        )}

        <Box flexDirection="row" justifyContent="flexEnd" gap="s8">
          {restrictedCommentsAvailable && !canUseRichTextEditor && (
            <VisibilityToggler
              value={form.values.visibility}
              onChange={(value) => form.setFieldValue('visibility', value)}
            />
          )}
          <Button type="secondary" disabled={form.isSubmitting} onClick={onEditFinish}>
            {i18n.t('common.cancel')}
          </Button>

          <Button disabled={form.isSubmitting || !form.isValid} submit loading={loading}>
            {i18n.t('common.save')}
          </Button>
        </Box>
      </Form>
    </FormikProvider>
  )
}
